import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Check initial session state
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      // No need to set loading here as the initial load handles it
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Memoize the auth methods
  const signUp = useCallback(async (data) => {
    const { email, password } = data;
    // Supabase signUp returns { data, error }
    return supabase.auth.signUp({ email, password });
  }, []);
  
  const signIn = useCallback(async (data) => {
    const { email, password } = data;
    // Supabase signInWithPassword returns { data, error }
    return supabase.auth.signInWithPassword({ email, password });
  }, []);
  
  const signOut = useCallback(async () =>
    // Supabase signOut returns { error }
     supabase.auth.signOut()
  , []);

  // Memoize the context value
  const value = useMemo(() => ({
    signUp,
    signIn,
    signOut,
    user,
    session,
    isAuthenticated: !!user,
    isLoading: loading,
  }), [signUp, signIn, signOut, user, session, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 